package org.lennycodes.hldi.pages.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.await
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*
import org.lennycodes.hldi.backend.FirebaseService
import org.lennycodes.hldi.backend.addDoc
import org.lennycodes.hldi.backend.collection
import org.lennycodes.hldi.backend.db_service.register
import org.lennycodes.hldi.backend.doc
import org.lennycodes.hldi.backend.setDoc
import org.lennycodes.hldi.constants.HLDITheme
import org.lennycodes.hldi.data.Admin

// ----- Styles -----

val DialogBackdropStyle = CssStyle {
    base {
        Modifier
            .fillMaxSize()
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("background", "${Color.rgba(0, 0, 0, 0.5f)}")
                property("backdrop-filter", "blur(4px)")
                property("display", "flex")
                property("justify-content", "center")
                property("align-items", "center")
                property("box-sizing", "border-box")
                property("z-index", "1000")
            }
    }
}

val DialogCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(420.px)
            .backgroundColor(Colors.White)
            .borderRadius(20.px)
            .styleModifier {
                property("box-shadow", "0 24px 60px ${Color.rgba(0, 0, 0, 0.3f)}")
                property("max-height", "90vh")
                property("overflow-y", "auto")
                property("box-sizing", "border-box")
            }
    }
}

val DialogTitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(20.px)
            .fontWeight(FontWeight.Bold)
            .color(HLDITheme.Colors.text)
            .margin(0.px, 0.px, 4.px)
    }
}

val DialogSubtitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(13.px)
            .color(HLDITheme.Colors.mediumGray)
            .margin(0.px, 0.px, 24.px)
    }
}

val FieldGroupStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(0.px, 0.px, 16.px)
            .display(DisplayStyle.Flex)
            .styleModifier { property("flex-direction", "column") }
    }
}

val FieldLabelStyle = CssStyle {
    base {
        Modifier
            .fontSize(13.px)
            .fontWeight(FontWeight.SemiBold)
            .color(HLDITheme.Colors.text)
            .margin(0.px, 0.px, 6.px)
    }
}

val FieldInputStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .fontSize(14.px)
            .styleModifier {
                property("border", "1.5px solid #E0E0E0")
                property("border-radius", "10px")
                property("outline", "none")
                property("box-sizing", "border-box")
                property("transition", "border-color 200ms ease, box-shadow 200ms ease")
            }
    }
    cssRule(":focus") {
        Modifier.styleModifier {
            property("border-color", "${HLDITheme.Colors.primary}")
            property("box-shadow", "0 0 0 3px ${Color.rgba(25, 118, 210, 0.15f)}")
        }
    }
}

val DialogActionsRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(8.px, 0.px, 0.px)
            .styleModifier { property("justify-content", "flex-end") }
    }
}

val CancelButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Colors.White)
            .color(HLDITheme.Colors.text)
            .fontSize(14.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "1.5px solid #E0E0E0")
                property("border-radius", "10px")
                property("transition", "background-color 150ms ease")
            }
    }
    hover {
        Modifier.styleModifier { property("background-color", "#F5F5F5") }
    }
}

val SaveButtonStyle = CssStyle {
    base {
        Modifier

            .backgroundColor(HLDITheme.Colors.primary)
            .color(Colors.White)
            .fontSize(14.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "none")
                property("border-radius", "10px")
                property("transition", "transform 150ms ease")
            }
    }
    hover {
        Modifier.styleModifier { property("transform", "translateY(-1px)") }
    }
}

// ----- Composable -----

@Composable
fun AddAdminDialog(
    onDismiss: () -> Unit,
    onSuccess: () -> Unit
) {

    val scope = rememberCoroutineScope()

    var isLoading by remember { mutableStateOf(false) }

    var fullName by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }

    var errorMessage by remember { mutableStateOf("") }


    Div(
        attrs = Modifier
            .padding(16.px)
            .then(DialogBackdropStyle.toModifier())
            .toAttrs {

                onClick {
                    if (!isLoading) {
                        onDismiss()
                    }
                }

            }
    ) {


        Div(
            attrs = Modifier
                .padding(32.px)
                .then(DialogCardStyle.toModifier())
                .toAttrs {

                    onClick {
                        it.stopPropagation()
                    }

                }
        ) {


            H3(
                attrs = DialogTitleStyle.toAttrs()
            ) {
                Text("New Administrator")
            }


            P(
                attrs = DialogSubtitleStyle.toAttrs()
            ) {
                Text("Create a new admin account with full access.")
            }

            // Full Name

            Div(
                attrs = FieldGroupStyle.toAttrs()
            ) {

                Label(
                    attrs = FieldLabelStyle.toAttrs()
                ) {

                    Text("Full Name")

                }


                Input(
                    type = InputType.Text,
                    attrs = Modifier
                        .padding(11.px,14.px)
                        .then(FieldInputStyle.toModifier())
                        .toAttrs {

                            placeholder("Loveness Leonard")

                            value(fullName)

                            onInput {
                                fullName = it.value
                            }


                            if(isLoading){
                                disabled()
                            }

                        }
                )

            }




            // Email

            Div(
                attrs = FieldGroupStyle.toAttrs()
            ){

                Label(
                    attrs = FieldLabelStyle.toAttrs()
                ){
                    Text("Email")
                }


                Input(
                    type = InputType.Email,
                    attrs = Modifier
                        .padding(11.px,14.px)
                        .then(FieldInputStyle.toModifier())
                        .toAttrs {

                            placeholder("sample@example.com")

                            value(email)

                            onInput {
                                email = it.value
                            }


                            if(isLoading){
                                disabled()
                            }

                        }
                )

            }





            // Password

            Div(
                attrs = FieldGroupStyle.toAttrs()
            ){

                Label(
                    attrs = FieldLabelStyle.toAttrs()
                ){

                    Text("Password")

                }


                Input(
                    type = InputType.Password,
                    attrs = Modifier
                        .padding(11.px,14.px)
                        .then(FieldInputStyle.toModifier())
                        .toAttrs {

                            placeholder("••••••••")

                            value(password)

                            onInput {
                                password = it.value
                            }


                            if(isLoading){
                                disabled()
                            }

                        }
                )

            }

            if (errorMessage.isNotEmpty()) {

                P(
                    attrs = Modifier
                        .color(Color.rgb(220, 53, 69))
                        .fontSize(14.px)
                        .margin(0.px, 0.px, 16.px)
                        .toAttrs()
                ) {

                    Text(errorMessage)

                }

            }



            Row(
                modifier = DialogActionsRowStyle.toModifier(),

                horizontalArrangement = Arrangement.spacedBy(12.px)
            ){



                Button(

                    enabled = !isLoading,

                    modifier = Modifier
                        .padding(11.px,22.px)
                        .then(CancelButtonStyle.toModifier()),


                    onClick = {

                        onDismiss()

                    }

                ){

                    Text("Cancel")

                }





                Button(

                    enabled = !isLoading,

                    modifier = Modifier
                        .padding(11.px,22.px)
                        .then(SaveButtonStyle.toModifier()),


                    onClick = {

                        scope.launch {

                            try {

                                errorMessage = ""


                                // Validate fields
                                if (fullName.isBlank()) {

                                    errorMessage = "Full name is required"
                                    return@launch

                                }


                                if (!isValidEmail(email)) {

                                    errorMessage = "Please enter a valid email address"
                                    return@launch

                                }


                                if (!isValidPassword(password)) {

                                    errorMessage =
                                        "Password must have 8+ characters, including uppercase, lowercase, and a number"

                                    return@launch

                                }


                                isLoading = true


                                // Create Firebase Auth account
                                val success = register(
                                    email.trim(),
                                    password
                                )


                                if (!success) {

                                    errorMessage = "Failed to create account"
                                    return@launch

                                }


                                console.log("Authentication account created")


                                val user = FirebaseService.auth.currentUser


                                if (user == null) {

                                    errorMessage = "Firebase user was not found"
                                    return@launch

                                }


                                console.log("User UID: ${user.uid}")


                                // Save profile data to Firestore
                                val usersCollection = collection(
                                    FirebaseService.db,
                                    "users"
                                )


                                val admin = Admin(
                                    uid = user.uid,
                                    fullName = fullName,
                                    email = email
                                )


// Document ID = email
                                val adminDocument = doc(
                                    usersCollection,
                                    email.lowercase()
                                )


                                setDoc(
                                    adminDocument,
                                    admin.toFirebaseData()
                                ).await()

                                console.log("Admin saved successfully")

                                onSuccess()   // refresh parent list

                                isLoading = false

                                // Close dialog
                                onDismiss()


                            } catch (e: Throwable) {


                                console.error(
                                    "Registration error: ${e.message}"
                                )


                                errorMessage =
                                    e.message ?: "Something went wrong"


                            } finally {


                                isLoading = false


                            }

                        }

                    }

                ){

                    Text(
                        if(isLoading)
                            "Saving..."
                        else
                            "Save"
                    )

                }


            }


        }

    }

}